import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { LuWifiOff } from 'react-icons/lu';
import AppErrorFallback from './AppErrorFallback';

export const FATAL_REQUEST_ERROR_EVENT = 'app:fatal-request-error';

// Fired by the apiAxios interceptor on timeouts / network drops / 5xx so the
// user gets a clear prompt to refresh instead of a dead page.
export const reportFatalRequestError = (message) => {
  window.dispatchEvent(new CustomEvent(FATAL_REQUEST_ERROR_EVENT, { detail: { message } }));
};

// contained=true → renders inside MainLayout's content column (sidebar + top
// navbar stay visible/usable), sitting just below the top navbar. Otherwise it
// covers the whole viewport (used outside the authenticated shell).
const GlobalRequestErrorOverlay = ({ contained }) => {
  const [error, setError] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      // Keep the first message; ignore the burst of follow-up failures
      setError((prev) => prev || { message: e.detail?.message });
    };
    window.addEventListener(FATAL_REQUEST_ERROR_EVENT, handler);
    return () => window.removeEventListener(FATAL_REQUEST_ERROR_EVENT, handler);
  }, []);

  // Lock page scroll while the prompt is up so there's no scrollbar behind it.
  useEffect(() => {
    if (!error) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [error]);

  if (!error) return null;

  // Contained: pin one viewport-height panel just below the top navbar (no
  // stretching to the full page height, so no inner scroll). Otherwise cover
  // the whole viewport.
  const positionClass = contained
    ? 'tw-absolute tw-left-0 tw-right-0 tw-z-[1000]'
    : 'tw-fixed tw-inset-0 tw-z-[2000]';
  const positionStyle = contained
    ? { top: 'var(--falcon-top-nav-height, 69px)', height: 'max(320px, calc(100dvh - var(--falcon-top-nav-height, 69px)))' }
    : undefined;

  return (
    <div
      className={`${positionClass} tw-flex tw-items-center tw-justify-center tw-bg-slate-50/80 tw-backdrop-blur-sm`}
      style={positionStyle}
    >
      <AppErrorFallback
        icon={LuWifiOff}
        title="Connection problem"
        message={error.message || 'We could not reach the server. Check your connection and refresh the page.'}
        onDismiss={() => setError(null)}
      />
    </div>
  );
};

GlobalRequestErrorOverlay.propTypes = { contained: PropTypes.bool };
GlobalRequestErrorOverlay.defaultProps = { contained: false };

export default GlobalRequestErrorOverlay;
